import { format, parseISO } from "date-fns"
import type {
  ContractTerm,
  ContractTermBasis,
  ContractTermInput,
  ContractTermStatus,
  ProgramRateCheck,
} from "@/api/contract_term"

export const BASIS_OPTIONS: { value: ContractTermBasis; label: string }[] = [
  { value: "pct_of_invoice_cost", label: "% of invoice cost" },
  { value: "pct_of_shorted_value", label: "% of shorted value" },
  { value: "flat_per_sku", label: "$ per SKU" },
  { value: "flat", label: "Flat $" },
]

export function is_percentage_basis(basis: ContractTermBasis): boolean {
  return basis === "pct_of_invoice_cost" || basis === "pct_of_shorted_value"
}

function to_cents(dollars: number): number {
  return Math.round(dollars * 100)
}

/** Dollars with thousands separators; cents only when there are any ("$30,000", "$3.90"). */
export function format_dollars(dollars: number): string {
  const cents = Math.abs(to_cents(dollars))
  const whole = cents % 100 === 0
  const text = (cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  })
  return `${dollars < 0 ? "-" : ""}$${text}`
}

/** A percent as the contract states it: up to 4 decimals, no trailing zeros ("1.17%", "10%"). */
export function format_percent(percent: number): string {
  return `${Number(percent.toFixed(4))}%`
}

/** The rate column: "1.17% of invoice cost", "$30,000 / SKU", plus any minimum and threshold. */
export function format_term_rate(term: Pick<ContractTerm, "basis" | "rate" | "minimum_amount" | "threshold_pct" | "threshold_window_weeks">): string {
  if (term.rate == null) return "—"
  let text: string
  switch (term.basis) {
    case "pct_of_invoice_cost":
      text = `${format_percent(term.rate)} of invoice cost`
      break
    case "pct_of_shorted_value":
      text = `${format_percent(term.rate)} of shorted value`
      break
    case "flat_per_sku":
      text = `${format_dollars(term.rate)} / SKU`
      break
    default:
      text = format_dollars(term.rate)
  }
  if (term.minimum_amount != null) {
    text += `, min ${format_dollars(term.minimum_amount)}/line`
  }
  if (term.threshold_pct != null) {
    const window = term.threshold_window_weeks != null ? ` over ${term.threshold_window_weeks} wks` : ""
    text += `, while < ${format_percent(term.threshold_pct)}${window}`
  }
  return text
}

function format_date(iso: string): string {
  return format(parseISO(iso), "MMM d, yyyy")
}

/** "Jul 11, 2023 onward", "Feb 12, 2020 – Dec 2, 2020", "Any date". */
export function format_effective_window(effective_from: string | null, effective_to: string | null): string {
  if (effective_from && effective_to) return `${format_date(effective_from)} – ${format_date(effective_to)}`
  if (effective_from) return `${format_date(effective_from)} onward`
  if (effective_to) return `Until ${format_date(effective_to)}`
  return "Any date"
}

/** Parse a typed amount ("$1,000", "8.75%", ""). `null` for empty, `undefined` when not a number. */
export function parse_optional_number(text: string): number | null | undefined {
  const cleaned = text.replace(/[$,%\s]/g, "")
  if (cleaned === "") return null
  const value = Number(cleaned)
  return Number.isFinite(value) ? value : undefined
}

/** The editor's form state: every field as the text the user typed. */
export interface ContractTermForm {
  program: string
  match_pattern: string
  retailer_name: string
  basis: ContractTermBasis
  rate: string
  minimum_amount: string
  threshold_pct: string
  threshold_window_weeks: string
  effective_from: string
  effective_to: string
  status: ContractTermStatus
  source_excerpt: string
  notes: string
}

export function empty_term_form(): ContractTermForm {
  return {
    program: "",
    match_pattern: "",
    retailer_name: "",
    basis: "pct_of_invoice_cost",
    rate: "",
    minimum_amount: "",
    threshold_pct: "",
    threshold_window_weeks: "",
    effective_from: "",
    effective_to: "",
    status: "confirmed",
    source_excerpt: "",
    notes: "",
  }
}

function number_text(value: number | null): string {
  return value == null ? "" : String(value)
}

export function term_to_form(term: ContractTerm): ContractTermForm {
  return {
    program: term.program,
    match_pattern: term.match_pattern ?? "",
    retailer_name: term.retailer_name ?? "",
    basis: term.basis,
    rate: number_text(term.rate),
    minimum_amount: number_text(term.minimum_amount),
    threshold_pct: number_text(term.threshold_pct),
    threshold_window_weeks: number_text(term.threshold_window_weeks),
    effective_from: term.effective_from ?? "",
    effective_to: term.effective_to ?? "",
    status: term.status,
    source_excerpt: term.source_excerpt ?? "",
    notes: term.notes ?? "",
  }
}

function text_or_null(text: string): string | null {
  const trimmed = text.trim()
  return trimmed === "" ? null : trimmed
}

/** Validate the form and build the request body (every field present, so it doubles as a PATCH). */
export function form_to_input(form: ContractTermForm): { input: ContractTermInput } | { error: string } {
  if (form.program.trim() === "") return { error: "Program is required." }
  const numbers: [keyof ContractTermForm, string][] = [
    ["rate", "Rate"],
    ["minimum_amount", "Minimum"],
    ["threshold_pct", "Threshold"],
    ["threshold_window_weeks", "Window"],
  ]
  const parsed: Record<string, number | null> = {}
  for (const [key, label] of numbers) {
    const value = parse_optional_number(form[key])
    if (value === undefined) return { error: `${label} must be a number.` }
    parsed[key] = value
  }
  if (parsed.rate == null) return { error: "Rate is required." }
  if (form.effective_from && form.effective_to && form.effective_to < form.effective_from) {
    return { error: "Effective to must be on or after effective from." }
  }
  return {
    input: {
      program: form.program.trim(),
      match_pattern: text_or_null(form.match_pattern),
      retailer_name: text_or_null(form.retailer_name),
      basis: form.basis,
      rate: parsed.rate,
      minimum_amount: parsed.minimum_amount,
      threshold_pct: parsed.threshold_pct,
      threshold_window_weeks: parsed.threshold_window_weeks == null ? null : Math.round(parsed.threshold_window_weeks),
      effective_from: text_or_null(form.effective_from),
      effective_to: text_or_null(form.effective_to),
      status: form.status,
      source_excerpt: text_or_null(form.source_excerpt),
      notes: text_or_null(form.notes),
    },
  }
}

/** "Charged 1.30% vs contracted 1.17% — $3.90 over". */
export function describe_program_rate_check(check: ProgramRateCheck): string {
  const rates = `Charged ${check.charged_rate.toFixed(2)}% vs contracted ${format_percent(check.contracted_rate)}`
  if (check.within_tolerance) return `${rates} — within tolerance`
  const over_cents = to_cents(check.charged_total) - to_cents(check.expected_total)
  const amount = format_dollars(Math.abs(over_cents) / 100)
  return over_cents > 0 ? `${rates} — ${amount} over` : `${rates} — ${amount} under`
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function program_check(overrides: Partial<ProgramRateCheck>): ProgramRateCheck {
    return {
      program_label: "LOBLAWS SCHF DC",
      term_id: "t1",
      term_program: "SCHF DC",
      term_status: "confirmed",
      effective_from: "2023-07-11",
      effective_to: null,
      source_excerpt: null,
      contracted_rate: 1.17,
      charged_rate: 1.3,
      charged_total: 39,
      basis_total: 3000,
      expected_total: 35.1,
      overcharge: 3.9,
      within_tolerance: false,
      line_count: 2,
      ...overrides,
    }
  }

  describe("format_term_rate", () => {
    it("reads the rate by basis", () => {
      const base = { minimum_amount: null, threshold_pct: null, threshold_window_weeks: null }
      expect(format_term_rate({ ...base, basis: "pct_of_invoice_cost", rate: 1.17 })).toBe("1.17% of invoice cost")
      expect(format_term_rate({ ...base, basis: "flat_per_sku", rate: 30000 })).toBe("$30,000 / SKU")
      expect(format_term_rate({ ...base, basis: "flat", rate: 750.5 })).toBe("$750.50")
      expect(format_term_rate({ ...base, basis: "flat", rate: null })).toBe("—")
    })

    it("includes the per-line minimum and threshold", () => {
      expect(
        format_term_rate({ basis: "pct_of_shorted_value", rate: 10, minimum_amount: 1000, threshold_pct: 98, threshold_window_weeks: 13 })
      ).toBe("10% of shorted value, min $1,000/line, while < 98% over 13 wks")
    })
  })

  describe("format_effective_window", () => {
    it("formats open and closed windows", () => {
      expect(format_effective_window("2023-07-11", null)).toBe("Jul 11, 2023 onward")
      expect(format_effective_window("2020-02-12", "2020-12-02")).toBe("Feb 12, 2020 – Dec 2, 2020")
      expect(format_effective_window(null, "2020-12-02")).toBe("Until Dec 2, 2020")
      expect(format_effective_window(null, null)).toBe("Any date")
    })
  })

  describe("parse_optional_number", () => {
    it("strips formatting and distinguishes empty from invalid", () => {
      expect(parse_optional_number("$1,000")).toBe(1000)
      expect(parse_optional_number(" 8.75% ")).toBe(8.75)
      expect(parse_optional_number("")).toBe(null)
      expect(parse_optional_number("abc")).toBe(undefined)
    })
  })

  describe("form_to_input", () => {
    it("builds a full body with nulls for cleared fields", () => {
      const form = { ...empty_term_form(), program: " SCHF DC ", rate: "1.17%", match_pattern: "SCHF\\s*DC", effective_from: "2023-07-11" }
      const result = form_to_input(form)
      expect("input" in result && result.input).toMatchObject({
        program: "SCHF DC",
        rate: 1.17,
        match_pattern: "SCHF\\s*DC",
        retailer_name: null,
        minimum_amount: null,
        effective_from: "2023-07-11",
        effective_to: null,
        status: "confirmed",
      })
    })

    it("rejects missing program, bad numbers and inverted dates", () => {
      expect(form_to_input({ ...empty_term_form(), rate: "1" })).toEqual({ error: "Program is required." })
      expect(form_to_input({ ...empty_term_form(), program: "O&A", rate: "lots" })).toEqual({ error: "Rate must be a number." })
      expect(form_to_input({ ...empty_term_form(), program: "O&A" })).toEqual({ error: "Rate is required." })
      expect(
        form_to_input({ ...empty_term_form(), program: "O&A", rate: "8.75", effective_from: "2023-07-11", effective_to: "2020-01-01" })
      ).toEqual({ error: "Effective to must be on or after effective from." })
    })

    it("round-trips a term", () => {
      const term: ContractTerm = {
        id: "t1",
        org_id: "o1",
        created_at: "2026-09-13T00:00:00Z",
        updated_at: "2026-09-13T00:00:00Z",
        contract_document_id: null,
        customer_id: null,
        retailer_name: "Loblaw",
        distributor: "unfi",
        program: "Fill rate fine",
        match_pattern: null,
        basis: "pct_of_shorted_value",
        rate: 10,
        minimum_amount: 1000,
        threshold_pct: 98,
        threshold_window_weeks: 13,
        effective_from: null,
        effective_to: null,
        status: "suggested",
        source_excerpt: "10% of the value of shorted quantity",
        notes: null,
      }
      const result = form_to_input(term_to_form(term))
      expect("input" in result && result.input).toMatchObject({
        retailer_name: "Loblaw",
        rate: 10,
        minimum_amount: 1000,
        threshold_pct: 98,
        threshold_window_weeks: 13,
        status: "suggested",
      })
    })
  })

  describe("describe_program_rate_check", () => {
    it("states the overcharge in dollars", () => {
      expect(describe_program_rate_check(program_check({}))).toBe("Charged 1.30% vs contracted 1.17% — $3.90 over")
    })

    it("says within tolerance and under", () => {
      expect(describe_program_rate_check(program_check({ charged_rate: 8.76, contracted_rate: 8.75, within_tolerance: true }))).toBe(
        "Charged 8.76% vs contracted 8.75% — within tolerance"
      )
      expect(
        describe_program_rate_check(program_check({ charged_rate: 1.0, charged_total: 30, expected_total: 35.1, overcharge: -5.1 }))
      ).toBe("Charged 1.00% vs contracted 1.17% — $5.10 under")
    })
  })
}
